import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../config/firebase';
import {
  ShoppingCart,
  Plus,
  FileText,
  TrendingDown,
  CheckCircle,
  XCircle,
  Clock,
  Search,
  AlertTriangle,
  DollarSign,
  Calendar,
  Package,
  Truck
} from 'lucide-react';
import { matchesSearchTerm } from '../utils/textSanitize';
import { formatQuantidadeComUnidade } from '../utils/numberFormat';

const CotacoesPage = () => {
  const { getEmpresaId, userData, isRole } = useAuth();
  const isFornecedor = isRole('fornecedor');

  // Estados
  const [loading, setLoading] = useState(false);
  const [solicitacoes, setSolicitacoes] = useState([]);
  const [stats, setStats] = useState(null);
  const [showNovaSolicitacao, setShowNovaSolicitacao] = useState(false);
  const [showCotacoesModal, setShowCotacoesModal] = useState(false);
  const [showResponderCotacao, setShowResponderCotacao] = useState(false); // ✅ FORNECEDOR
  const [solicitacaoSelecionada, setSolicitacaoSelecionada] = useState(null);
  const [cotacoesDaSolicitacao, setCotacoesDaSolicitacao] = useState([]);
  const [materiasPrimas, setMateriasPrimas] = useState([]);

  // Form nova solicitação
  const [formData, setFormData] = useState({
    materia_prima_id: '',
    quantidade_solicitada: '',
    motivo: 'manual',
    dias_para_resposta: 7
  });

  // ✅ FORNECEDOR: Form responder cotação
  const [cotacaoFormData, setCotacaoFormData] = useState({
    preco_unitario: '',
    prazo_entrega_dias: '',
    condicoes_pagamento: '',
    validade_cotacao_dias: 30,
    observacoes: ''
  });

  const [filtroStatus, setFiltroStatus] = useState('todos');
  const [searchTerm, setSearchTerm] = useState('');

  // ==================== CARREGAR DADOS ====================

  useEffect(() => {
    loadSolicitacoes();
    loadStats();
    loadMateriasPrimas();
  }, []);

  const loadSolicitacoes = async () => {
    setLoading(true);
    try {
      if (isFornecedor) {
        // ✅ FORNECEDOR: Buscar solicitações onde foi contatado
        const listSolicitacoesParaFornecedor = httpsCallable(functions, 'listSolicitacoesParaFornecedor');
        const result = await listSolicitacoesParaFornecedor({
          fornecedor_id: userData?.id_fornecedor,
          limit: 100
        });

        if (result.data.success) {
          setSolicitacoes(result.data.data.items || []);
        }
      } else {
        // EMPRESA: Buscar suas solicitações
        const listSolicitacoesCotacao = httpsCallable(functions, 'listSolicitacoesCotacao');
        const result = await listSolicitacoesCotacao({
          empresa_id: getEmpresaId(),
          limit: 100
        });

        if (result.data.success) {
          setSolicitacoes(result.data.data.items || []);
        }
      }
    } catch (error) {
      console.error('Erro ao carregar solicitações:', error);
      alert('Erro ao carregar solicitações');
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const getCotacoesStats = httpsCallable(functions, 'getCotacoesStats');
      const result = await getCotacoesStats({
        empresa_id: getEmpresaId()
      });

      if (result.data.success) {
        setStats(result.data.data);
      }
    } catch (error) {
      console.error('Erro ao carregar estatísticas:', error);
    }
  };

  const loadMateriasPrimas = async () => {
    try {
      const listProducts = httpsCallable(functions, 'listProducts');
      const result = await listProducts({
        empresa_id: getEmpresaId(),
        limit: 1000
      });

      if (result.data.success) {
        setMateriasPrimas(result.data.data.items || []);
      }
    } catch (error) {
      console.error('Erro ao carregar matérias-primas:', error);
    }
  };

  // ==================== VERIFICAR ESTOQUE BAIXO ====================

  const handleVerificarEstoqueBaixo = async () => {
    setLoading(true);
    try {
      const verificarEstoqueBaixo = httpsCallable(functions, 'verificarEstoqueBaixo');
      const result = await verificarEstoqueBaixo({
        empresa_id: getEmpresaId()
      });

      if (result.data.success) {
        const data = result.data.data;
        alert(
          `✅ Verificação concluída!\n\n` +
          `Itens com estoque baixo: ${data.itens_estoque_baixo}\n` +
          `Cotações criadas: ${data.cotacoes_criadas}\n\n` +
          `${data.message}`
        );
        
        if (data.cotacoes_criadas > 0) {
          loadSolicitacoes();
          loadStats();
        }
      }
    } catch (error) {
      console.error('Erro ao verificar estoque:', error);
      alert('Erro ao verificar estoque baixo');
    } finally {
      setLoading(false);
    }
  };

  // ==================== CRIAR SOLICITAÇÃO ====================

  const handleCriarSolicitacao = async (e) => {
    e.preventDefault();
    
    if (!formData.materia_prima_id) {
      alert('Selecione um ingrediente');
      return;
    }

    if (!formData.quantidade_solicitada || formData.quantidade_solicitada <= 0) {
      alert('Quantidade inválida');
      return;
    }

    setLoading(true);
    try {
      const criarSolicitacaoCotacao = httpsCallable(functions, 'criarSolicitacaoCotacao');
      const result = await criarSolicitacaoCotacao({
        empresa_id: getEmpresaId(),
        ...formData,
        quantidade_solicitada: parseFloat(formData.quantidade_solicitada)
      });

      if (result.data.success) {
        alert('✅ ' + result.data.data.message);
        setShowNovaSolicitacao(false);
        setFormData({
          materia_prima_id: '',
          quantidade_solicitada: '',
          motivo: 'manual',
          dias_para_resposta: 7
        });
        loadSolicitacoes();
        loadStats();
      }
    } catch (error) {
      console.error('Erro ao criar solicitação:', error);
      alert('Erro ao criar solicitação');
    } finally {
      setLoading(false);
    }
  };

  // ==================== VISUALIZAR COTAÇÕES ====================

  const handleVisualizarCotacoes = async (solicitacao) => {
    setSolicitacaoSelecionada(solicitacao);
    setShowCotacoesModal(true);
    setLoading(true);

    try {
      const listCotacoesDaSolicitacao = httpsCallable(functions, 'listCotacoesDaSolicitacao');
      const result = await listCotacoesDaSolicitacao({
        solicitacao_cotacao_id: solicitacao.id
      });

      if (result.data.success) {
        setCotacoesDaSolicitacao(result.data.data.items || []);
      }
    } catch (error) {
      console.error('Erro ao carregar cotações:', error);
      alert('Erro ao carregar cotações');
    } finally {
      setLoading(false);
    }
  };

  // ==================== ACEITAR COTAÇÃO ====================

  const handleAceitarCotacao = async (cotacaoId) => {
    if (!window.confirm('Confirma aceitar esta cotação?')) return;

    setLoading(true);
    try {
      const aceitarCotacao = httpsCallable(functions, 'aceitarCotacao');
      const result = await aceitarCotacao({
        cotacao_id: cotacaoId,
        criar_pedido: false // Sprint 6
      });

      if (result.data.success) {
        alert('✅ Cotação aceita com sucesso!');
        setShowCotacoesModal(false);
        loadSolicitacoes();
        loadStats();
      }
    } catch (error) {
      console.error('Erro ao aceitar cotação:', error);
      alert('Erro ao aceitar cotação');
    } finally {
      setLoading(false);
    }
  };

  // ==================== FORNECEDOR: RESPONDER COTAÇÃO ====================

  const handleResponderCotacao = async (e) => {
    e.preventDefault();

    if (!solicitacaoSelecionada) return;

    // Validações
    if (!cotacaoFormData.preco_unitario || parseFloat(cotacaoFormData.preco_unitario) <= 0) {
      alert('Preço unitário inválido');
      return;
    }

    if (!cotacaoFormData.prazo_entrega_dias || parseInt(cotacaoFormData.prazo_entrega_dias) <= 0) {
      alert('Prazo de entrega inválido');
      return;
    }

    setLoading(true);
    try {
      const registrarCotacao = httpsCallable(functions, 'registrarCotacao');
      const result = await registrarCotacao({
        solicitacao_cotacao_id: solicitacaoSelecionada.id,
        fornecedor_id: userData?.id_fornecedor,
        preco_unitario: parseFloat(cotacaoFormData.preco_unitario),
        prazo_entrega_dias: parseInt(cotacaoFormData.prazo_entrega_dias),
        condicoes_pagamento: cotacaoFormData.condicoes_pagamento || 'A negociar',
        validade_cotacao_dias: parseInt(cotacaoFormData.validade_cotacao_dias) || 30,
        observacoes: cotacaoFormData.observacoes
      });

      if (result.data.success) {
        alert('✅ Cotação enviada com sucesso!');
        setShowResponderCotacao(false);
        setSolicitacaoSelecionada(null);
        setCotacaoFormData({
          preco_unitario: '',
          prazo_entrega_dias: '',
          condicoes_pagamento: '',
          validade_cotacao_dias: 30,
          observacoes: ''
        });
        loadSolicitacoes();
      }
    } catch (error) {
      console.error('Erro ao responder cotação:', error);
      alert('Erro ao enviar cotação');
    } finally {
      setLoading(false);
    }
  };

  // ==================== FILTROS ====================

  const solicitacoesFiltradas = solicitacoes.filter(s => {
    if (filtroStatus !== 'todos' && s.status !== filtroStatus) return false;
    if (searchTerm && !matchesSearchTerm(s.materia_prima_nome, searchTerm)) return false;
    return true;
  });

  // ==================== STATUS BADGE ====================

  const getStatusBadge = (status) => {
    const badges = {
      pendente: { color: 'bg-yellow-100 text-yellow-800', icon: Clock, label: 'Pendente' },
      cotacoes_recebidas: { color: 'bg-blue-100 text-blue-800', icon: FileText, label: 'Cotações Recebidas' },
      pedido_gerado: { color: 'bg-green-100 text-green-800', icon: CheckCircle, label: 'Pedido Gerado' },
      cancelada: { color: 'bg-red-100 text-red-800', icon: XCircle, label: 'Cancelada' }
    };

    const badge = badges[status] || badges.pendente;
    const Icon = badge.icon;

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${badge.color}`}>
        <Icon className="h-3 w-3 mr-1" />
        {badge.label}
      </span>
    );
  };

  // ==================== RENDER ====================

  if (loading && !solicitacoes.length) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Carregando cotações...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <ShoppingCart className="h-8 w-8 text-blue-600" />
              {isFornecedor ? 'Solicitações de Cotação' : 'Cotações de Compras'}
            </h1>
            <p className="text-gray-600 mt-1">
              {isFornecedor 
                ? 'Responda às solicitações de cotação de seus clientes' 
                : 'Gestão de solicitações e comparação de cotações'}
            </p>
          </div>

          {!isFornecedor && (
            <div className="flex gap-3">
              <button
                onClick={handleVerificarEstoqueBaixo}
                disabled={loading}
                className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors flex items-center gap-2 disabled:opacity-50"
              >
                <TrendingDown className="h-5 w-5" />
                Verificar Estoque Baixo
              </button>

              <button
                onClick={() => setShowNovaSolicitacao(true)}
                disabled={loading}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2 disabled:opacity-50"
              >
                <Plus className="h-5 w-5" />
                Nova Solicitação
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Dashboard de Estatísticas */}
      {!isFornecedor && stats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total de Solicitações</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">{stats.total_solicitacoes}</p>
              </div>
              <FileText className="h-10 w-10 text-blue-600 opacity-20" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Pendentes</p>
                <p className="text-2xl font-bold text-yellow-600 mt-1">{stats.pendentes}</p>
              </div>
              <Clock className="h-10 w-10 text-yellow-600 opacity-20" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Com Cotações</p>
                <p className="text-2xl font-bold text-blue-600 mt-1">{stats.com_cotacoes}</p>
              </div>
              <DollarSign className="h-10 w-10 text-blue-600 opacity-20" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Pedidos Gerados</p>
                <p className="text-2xl font-bold text-green-600 mt-1">{stats.pedidos_gerados}</p>
              </div>
              <CheckCircle className="h-10 w-10 text-green-600 opacity-20" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Cotações Recebidas</p>
                <p className="text-2xl font-bold text-purple-600 mt-1">{stats.total_cotacoes_recebidas}</p>
              </div>
              <Truck className="h-10 w-10 text-purple-600 opacity-20" />
            </div>
          </div>
        </div>
      )}

      {/* Filtros */}
      {!isFornecedor && (
      <div className="bg-white rounded-lg shadow-sm p-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
            <select
              value={filtroStatus}
              onChange={(e) => setFiltroStatus(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="todos">Todos os Status</option>
              <option value="pendente">Pendente</option>
              <option value="cotacoes_recebidas">Cotações Recebidas</option>
              <option value="pedido_gerado">Pedido Gerado</option>
              <option value="cancelada">Cancelada</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Buscar Ingrediente</label>
            <div className="relative">
              <Search className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Digite o nome do ingrediente..."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
        </div>
      </div>
      )}

      {/* Lista de Solicitações */}
      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="text-left py-3 px-4 font-medium text-gray-900">Ingrediente</th>
                <th className="text-left py-3 px-4 font-medium text-gray-900">Quantidade</th>
                {!isFornecedor && <th className="text-left py-3 px-4 font-medium text-gray-900">Motivo</th>}
                {!isFornecedor && <th className="text-left py-3 px-4 font-medium text-gray-900">Status</th>}
                {!isFornecedor && <th className="text-left py-3 px-4 font-medium text-gray-900">Fornecedores</th>}
                {!isFornecedor && <th className="text-left py-3 px-4 font-medium text-gray-900">Cotações</th>}
                <th className="text-left py-3 px-4 font-medium text-gray-900">Data Solicitação</th>
                <th className="text-left py-3 px-4 font-medium text-gray-900">Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {solicitacoesFiltradas.length === 0 ? (
                <tr>
                  <td colSpan="8" className="py-8 text-center text-gray-500">
                    Nenhuma solicitação encontrada
                  </td>
                </tr>
              ) : (
                solicitacoesFiltradas.map((solicitacao) => (
                  <tr key={solicitacao.id} className="hover:bg-gray-50">
                    <td className="py-3 px-4 text-gray-900 font-medium">
                      {solicitacao.materia_prima_nome}
                    </td>
                    <td className="py-3 px-4 text-gray-900">
                      {formatQuantidadeComUnidade(solicitacao.quantidade_solicitada, solicitacao.unidade)}
                    </td>
                    {!isFornecedor && (
                      <td className="py-3 px-4">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                          solicitacao.motivo === 'estoque_baixo' 
                            ? 'bg-red-100 text-red-800' 
                            : solicitacao.motivo === 'manual'
                            ? 'bg-gray-100 text-gray-800'
                            : 'bg-blue-100 text-blue-800'
                        }`}>
                          {solicitacao.motivo === 'estoque_baixo' ? (
                            <>
                              <AlertTriangle className="h-3 w-3 mr-1" />
                              Estoque Baixo
                            </>
                          ) : solicitacao.motivo === 'manual' ? (
                            'Manual'
                          ) : (
                            'Novo Produto'
                          )}
                        </span>
                      </td>
                    )}
                    {!isFornecedor && (
                      <td className="py-3 px-4">
                        {getStatusBadge(solicitacao.status)}
                      </td>
                    )}
                    {!isFornecedor && (
                      <td className="py-3 px-4 text-gray-900">
                        {solicitacao.fornecedores_contatados?.length || 0}
                      </td>
                    )}
                    {!isFornecedor && (
                      <td className="py-3 px-4">
                        <span className={`font-semibold ${
                          solicitacao.numero_cotacoes_recebidas > 0 ? 'text-green-600' : 'text-gray-400'
                        }`}>
                          {solicitacao.numero_cotacoes_recebidas || 0}
                        </span>
                      </td>
                    )}
                    <td className="py-3 px-4 text-gray-600">
                      {solicitacao.data_solicitacao?.toDate?.()?.toLocaleDateString('pt-BR') || 'N/A'}
                    </td>
                    <td className="py-3 px-4">
                      {isFornecedor ? (
                        // ✅ FORNECEDOR: Botão Responder
                        solicitacao.ja_respondido ? (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            <CheckCircle className="h-3 w-3 mr-1" />
                            Respondido
                          </span>
                        ) : (
                          <button
                            onClick={() => {
                              setSolicitacaoSelecionada(solicitacao);
                              setShowResponderCotacao(true);
                            }}
                            className="px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 font-medium text-sm"
                          >
                            Responder
                          </button>
                        )
                      ) : (
                        // EMPRESA: Botão Ver Cotações
                        solicitacao.numero_cotacoes_recebidas > 0 && (
                          <button
                            onClick={() => handleVisualizarCotacoes(solicitacao)}
                            className="text-blue-600 hover:text-blue-800 font-medium text-sm"
                          >
                            Ver Cotações
                          </button>
                        )
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal Nova Solicitação */}
      {showNovaSolicitacao && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <Plus className="h-6 w-6 text-blue-600" />
                Nova Solicitação de Cotação
              </h2>

              <form onSubmit={handleCriarSolicitacao} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Ingrediente *
                  </label>
                  <select
                    value={formData.materia_prima_id}
                    onChange={(e) => setFormData(prev => ({ ...prev, materia_prima_id: e.target.value }))}
                    required
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Selecione um ingrediente</option>
                    {materiasPrimas.map(mp => (
                      <option key={mp.id} value={mp.id}>{mp.nome}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Quantidade Solicitada *
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.quantidade_solicitada}
                    onChange={(e) => setFormData(prev => ({ ...prev, quantidade_solicitada: e.target.value }))}
                    required
                    min="0.01"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Ex: 100"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Motivo
                  </label>
                  <select
                    value={formData.motivo}
                    onChange={(e) => setFormData(prev => ({ ...prev, motivo: e.target.value }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="manual">Manual</option>
                    <option value="estoque_baixo">Estoque Baixo</option>
                    <option value="novo_produto">Novo Produto</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Prazo para Resposta (dias)
                  </label>
                  <input
                    type="number"
                    value={formData.dias_para_resposta}
                    onChange={(e) => setFormData(prev => ({ ...prev, dias_para_resposta: parseInt(e.target.value) }))}
                    min="1"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div className="flex justify-end gap-3 pt-4 border-t">
                  <button
                    type="button"
                    onClick={() => setShowNovaSolicitacao(false)}
                    className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    disabled={loading}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                  >
                    {loading ? 'Criando...' : 'Criar Solicitação'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Modal Visualizar Cotações */}
      {showCotacoesModal && solicitacaoSelecionada && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                    <FileText className="h-6 w-6 text-blue-600" />
                    Cotações Recebidas
                  </h2>
                  <p className="text-gray-600 mt-1">
                    {solicitacaoSelecionada.materia_prima_nome} - {formatQuantidadeComUnidade(solicitacaoSelecionada.quantidade_solicitada, solicitacaoSelecionada.unidade)}
                  </p>
                </div>
                <button
                  onClick={() => setShowCotacoesModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XCircle className="h-6 w-6" />
                </button>
              </div>

              {loading ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
                  <p className="mt-4 text-gray-600">Carregando cotações...</p>
                </div>
              ) : cotacoesDaSolicitacao.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  Nenhuma cotação recebida ainda
                </div>
              ) : (
                <div className="space-y-4">
                  {cotacoesDaSolicitacao.map((cotacao, index) => (
                    <div
                      key={cotacao.id}
                      className={`border-2 rounded-lg p-4 ${
                        index === 0 
                          ? 'border-green-500 bg-green-50' 
                          : 'border-gray-200'
                      } ${
                        cotacao.aceita 
                          ? 'bg-blue-50 border-blue-500' 
                          : ''
                      }`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-3">
                            <h3 className="font-semibold text-gray-900 text-lg">
                              {cotacao.fornecedor_nome}
                            </h3>
                            {index === 0 && !cotacao.aceita && (
                              <span className="bg-green-100 text-green-800 text-xs font-medium px-2.5 py-0.5 rounded">
                                Melhor Preço
                              </span>
                            )}
                            {cotacao.aceita && (
                              <span className="bg-blue-100 text-blue-800 text-xs font-medium px-2.5 py-0.5 rounded flex items-center gap-1">
                                <CheckCircle className="h-3 w-3" />
                                Aceita
                              </span>
                            )}
                          </div>

                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            <div>
                              <p className="text-sm text-gray-600">Preço Unitário</p>
                              <p className="text-lg font-bold text-gray-900">
                                R$ {cotacao.preco_unitario?.toFixed(2) || '0.00'}
                              </p>
                            </div>
                            <div>
                              <p className="text-sm text-gray-600">Preço Total</p>
                              <p className="text-lg font-bold text-blue-600">
                                R$ {cotacao.preco_total?.toFixed(2) || '0.00'}
                              </p>
                            </div>
                            <div>
                              <p className="text-sm text-gray-600">Prazo de Entrega</p>
                              <p className="text-gray-900 font-medium">
                                {cotacao.prazo_entrega_dias} dias
                              </p>
                            </div>
                            <div>
                              <p className="text-sm text-gray-600">Validade</p>
                              <p className="text-gray-900 font-medium">
                                {cotacao.validade_cotacao_dias} dias
                              </p>
                            </div>
                          </div>

                          <div className="mt-3">
                            <p className="text-sm text-gray-600">Condições de Pagamento</p>
                            <p className="text-gray-900">{cotacao.condicoes_pagamento}</p>
                          </div>

                          {cotacao.observacoes && (
                            <div className="mt-3">
                              <p className="text-sm text-gray-600">Observações</p>
                              <p className="text-gray-900 text-sm">{cotacao.observacoes}</p>
                            </div>
                          )}
                        </div>

                        {!cotacao.aceita && solicitacaoSelecionada.status !== 'pedido_gerado' && (
                          <button
                            onClick={() => handleAceitarCotacao(cotacao.id)}
                            disabled={loading}
                            className="ml-4 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 flex items-center gap-2"
                          >
                            <CheckCircle className="h-4 w-4" />
                            Aceitar
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <div className="flex justify-end pt-4 border-t mt-6">
                <button
                  onClick={() => setShowCotacoesModal(false)}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Fechar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ✅ MODAL RESPONDER COTAÇÃO (FORNECEDOR) */}
      {showResponderCotacao && solicitacaoSelecionada && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <FileText className="h-6 w-6 text-blue-600" />
                Responder Solicitação de Cotação
              </h2>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                <h3 className="font-semibold text-blue-900 mb-2">Informações da Solicitação</h3>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <p className="text-blue-700"><strong>Ingrediente:</strong> {solicitacaoSelecionada.materia_prima_nome}</p>
                  </div>
                  <div>
                    <p className="text-blue-700"><strong>Quantidade:</strong> {formatQuantidadeComUnidade(solicitacaoSelecionada.quantidade_solicitada, solicitacaoSelecionada.unidade)}</p>
                  </div>
                  <div>
                    <p className="text-blue-700"><strong>Data Limite:</strong> {solicitacaoSelecionada.data_limite_resposta?.toDate?.()?.toLocaleDateString('pt-BR') || 'N/A'}</p>
                  </div>
                </div>
              </div>

              <form onSubmit={handleResponderCotacao} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Preço Unitário (R$) *
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      min="0.01"
                      value={cotacaoFormData.preco_unitario}
                      onChange={(e) => setCotacaoFormData(prev => ({ ...prev, preco_unitario: e.target.value }))}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Ex: 15.50"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Prazo de Entrega (dias) *
                    </label>
                    <input
                      type="number"
                      min="1"
                      value={cotacaoFormData.prazo_entrega_dias}
                      onChange={(e) => setCotacaoFormData(prev => ({ ...prev, prazo_entrega_dias: e.target.value }))}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Ex: 7"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Condições de Pagamento
                  </label>
                  <input
                    type="text"
                    value={cotacaoFormData.condicoes_pagamento}
                    onChange={(e) => setCotacaoFormData(prev => ({ ...prev, condicoes_pagamento: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Ex: 30 dias, À vista, Parcelado"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Validade da Cotação (dias)
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={cotacaoFormData.validade_cotacao_dias}
                    onChange={(e) => setCotacaoFormData(prev => ({ ...prev, validade_cotacao_dias: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Ex: 30"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Observações
                  </label>
                  <textarea
                    value={cotacaoFormData.observacoes}
                    onChange={(e) => setCotacaoFormData(prev => ({ ...prev, observacoes: e.target.value }))}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Informações adicionais sobre sua cotação..."
                  />
                </div>

                {cotacaoFormData.preco_unitario && solicitacaoSelecionada.quantidade_solicitada && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <p className="text-sm text-green-700 mb-1">Preço Total Estimado:</p>
                    <p className="text-2xl font-bold text-green-900">
                      R$ {(parseFloat(cotacaoFormData.preco_unitario) * solicitacaoSelecionada.quantidade_solicitada).toFixed(2)}
                    </p>
                  </div>
                )}

                <div className="flex justify-end gap-3 pt-4 border-t">
                  <button
                    type="button"
                    onClick={() => {
                      setShowResponderCotacao(false);
                      setSolicitacaoSelecionada(null);
                      setCotacaoFormData({
                        preco_unitario: '',
                        prazo_entrega_dias: '',
                        condicoes_pagamento: '',
                        validade_cotacao_dias: 30,
                        observacoes: ''
                      });
                    }}
                    className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    disabled={loading}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center gap-2"
                  >
                    {loading ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        Enviando...
                      </>
                    ) : (
                      <>
                        <CheckCircle className="h-4 w-4" />
                        Enviar Cotação
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CotacoesPage;

